/**
 * Live competition updates.
 *
 * The problem: a player is watching the grid on a Saturday afternoon and their team's match
 * has just finished. Until something re-fetches, their row says "pending" — and re-fetching
 * the grid, the leaderboard, the round's picks and the competition itself on a timer, for
 * every viewer, all week, is a lot of traffic to discover that nothing has happened.
 *
 * So the client polls one cheap endpoint instead. `GET /competitions/:id/live` answers in a
 * single query with a version string that moves whenever anything a player would see changes.
 * When it moves, everything else is invalidated at once and React Query re-fetches only what
 * is actually mounted. When it doesn't, the poll cost one small request.
 *
 * The same endpoint also nudges the server into pulling fresh results from the fixture
 * provider while a round is in play (rate-limited per league, shared with the cron), so the
 * act of watching is what makes results land quickly — without every viewer costing a call to
 * ESPN.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type LiveState, type RefreshOutcome } from "./api";

/**
 * How often to ask, by what's happening.
 *
 * These are deliberately not one number. During a match a stale grid is the whole complaint,
 * and ten seconds of latency is worth the request; on a Wednesday in July there is nothing to
 * find and the poll should be close to free. `false` stops polling altogether.
 */
const INTERVALS = {
  /** A match is being played right now. */
  live: 12_000,
  /** Kick-off is imminent, or a match has started and is yet to report a result. */
  imminent: 20_000,
  /**
   * A round is in play but the next kick-off is still a while away. Longer than Neon's 5-minute
   * idle suspend, so an open tab between matches lets the database sleep; refocusing the tab
   * refetches immediately anyway.
   */
  upcoming: 600_000,
  /** Nothing to play. Same reasoning as `upcoming`. */
  idle: 600_000,
  /** The competition is over. Nothing will change again. */
  finished: false,
} as const;

/** How close to kick-off counts as imminent. */
const IMMINENT_MS = 20 * 60 * 1000;

export function pollInterval(state: LiveState | undefined): number | false {
  if (!state) return INTERVALS.upcoming;
  if (state.competitionStatus === "complete") return INTERVALS.finished;
  if (state.liveFixtures > 0) return INTERVALS.live;
  if (!state.pendingFixtures) return INTERVALS.idle;

  // Null once every pending fixture has kicked off — so either a match is under way and the
  // feed hasn't said so yet, or one should have finished and hasn't. Both want a close eye.
  if (!state.nextKickoffAt) return INTERVALS.imminent;

  const untilKickoff = new Date(state.nextKickoffAt).getTime() - Date.now();
  return untilKickoff <= IMMINENT_MS ? INTERVALS.imminent : INTERVALS.upcoming;
}

/**
 * Everything whose contents depend on results landing.
 *
 * React Query matches query keys by prefix, so `["picks", id]` invalidates every round's picks
 * without this needing to know which rounds exist or which one is mounted.
 */
function dependentKeys(competitionId: string): unknown[][] {
  return [
    ["competition", competitionId],
    ["results-grid", competitionId],
    ["leaderboard", competitionId],
    ["rounds", competitionId],
    ["picks", competitionId],
    ["fixtures", competitionId],
    ["teams", competitionId],
  ];
}

export interface LiveCompetition {
  state: LiveState | undefined;
  /** A match in this competition is being played right now. */
  isLive: boolean;
  /** A manual refresh is in flight — for a spinner on a refresh control. */
  isRefreshing: boolean;
  /** When this client last saw the state change. Null until it does. */
  lastChangeAt: Date | null;
  /**
   * Ask the server to pull results now and settle anything ready, then refresh the view.
   * Resolves once the new state is in hand.
   */
  refreshNow: () => Promise<RefreshOutcome | undefined>;
}

/**
 * Keeps a competition's views in step with reality.
 *
 * Mount this once per competition screen. It owns the polling; every other query on the page
 * stays a plain `useQuery` with no interval of its own, and is re-fetched when — and only
 * when — the server says something changed.
 */
export function useCompetitionLive(
  competitionId: string,
  options: { enabled?: boolean } = {},
): LiveCompetition {
  const queryClient = useQueryClient();
  const enabled = (options.enabled ?? true) && Boolean(competitionId);

  const { data: state } = useQuery({
    queryKey: ["live", competitionId],
    queryFn: ({ signal }) => api.competitions.live(competitionId, signal),
    enabled,
    /**
     * Always fetched, never served from cache: the whole point of this query is to be newer
     * than the last answer, and a "fresh" cached version defeats it.
     */
    staleTime: 0,
    refetchInterval: ({ state: queryState }) => pollInterval(queryState.data),
    /**
     * Left at the default (false): a backgrounded tab shouldn't poll. Returning to the tab
     * re-fetches on focus, so the first thing a player sees on coming back is current.
     */
    refetchIntervalInBackground: false,
    /**
     * A poll failing is not worth a visible error — the next one is seconds away, and the
     * page's own queries surface anything that actually matters.
     */
    throwOnError: false,
    retry: 1,
  });

  const version = state?.version;
  /**
   * The last version this client acted on, tagged with the competition it belongs to.
   *
   * Both halves in one ref, deliberately: a separate "reset when the id changes" effect races
   * the one below on the first render after a navigation, and carrying the previous
   * competition's version across would make the new screen's first poll look like a change.
   */
  const seen = useRef<{ competitionId: string; version: string } | null>(null);
  // State rather than a ref: this one is rendered, so it has to cause a re-render when it moves.
  const [lastChangeAt, setLastChangeAt] = useState<Date | null>(null);

  const adopt = useCallback(
    (next: string, changed: boolean) => {
      seen.current = { competitionId, version: next };
      if (!changed) return;

      setLastChangeAt(new Date());
      for (const key of dependentKeys(competitionId)) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
    [competitionId, queryClient],
  );

  useEffect(() => {
    if (!version) return;

    const previous = seen.current;
    // A first answer — on mount, or on arriving at a different competition — is the baseline,
    // not a change: the page has just loaded this data for itself.
    const isBaseline = !previous || previous.competitionId !== competitionId;
    if (!isBaseline && previous.version === version) return;

    adopt(version, !isBaseline);
  }, [version, competitionId, adopt]);

  useEffect(() => {
    setLastChangeAt(null);
  }, [competitionId]);

  const refresh = useMutation({
    mutationFn: () => api.competitions.refresh(competitionId),
    onSuccess: (outcome) => {
      // Seed the live query with the state the refresh just produced, so the next poll compares
      // against it rather than re-announcing the change this call already handled.
      queryClient.setQueryData(["live", competitionId], outcome.state);
      adopt(outcome.state.version, true);
    },
  });

  const { mutateAsync } = refresh;
  const refreshNow = useCallback(async () => {
    try {
      return await mutateAsync();
    } catch {
      // The caller gets `undefined`; the page's own queries still show whatever is current.
      return undefined;
    }
  }, [mutateAsync]);

  return {
    state,
    isLive: (state?.liveFixtures ?? 0) > 0,
    isRefreshing: refresh.isPending,
    lastChangeAt,
    refreshNow,
  };
}
